// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CtsGantryFunctionalUnit } from '../interfaces/cts-gantry-functional-unit';
import { CtsGantryFunctionalUnitCheck } from '../interfaces/cts-gantry-functional-unit-check';
import { CtsGantryFunctionalUnitData } from '../interfaces/cts-gantry-functional-unit-data';
import { CtsGantryFunctionalUnitStep } from '../interfaces/cts-gantry-functional-unit-step';

export class CtsGantryFnUnitSelectionModel {
  private fnUnitNameSet = new Set<string>();
  private functionalUnitNameList: string[] = [];

  private first: CtsGantryFunctionalUnitData | undefined;
  private second: CtsGantryFunctionalUnitData | undefined;

  private readonly firstMap = new Map<string, CtsGantryFunctionalUnit>();
  private readonly secondMap = new Map<string, CtsGantryFunctionalUnit>();

  private firstFnUnitCheckList: CtsGantryFunctionalUnitCheck[] = [];
  private secondFnUnitCheckList: CtsGantryFunctionalUnitCheck[] = [];

  public updateFirst = (value: CtsGantryFunctionalUnitData): void => {
    this.first = value;
    this.initFnUnitNameSet();
  };

  public updateSecond = (value: CtsGantryFunctionalUnitData): void => {
    this.second = value;
    this.initFnUnitNameSet();
  };

  public getFirstFnUnitCheckList(): CtsGantryFunctionalUnitCheck[] {
    return this.firstFnUnitCheckList;
  }

  public getSecondFnUnitCheckList(): CtsGantryFunctionalUnitCheck[] {
    return this.secondFnUnitCheckList;
  }

  public getFirst(): CtsGantryFunctionalUnitData | undefined {
    return this.first;
  }

  public getSecond(): CtsGantryFunctionalUnitData | undefined {
    return this.second;
  }

  public getFirstFnUnitSteps(): CtsGantryFunctionalUnitStep[] {
    return this.first ? this.first.functionalUnits.steps : [];
  }

  public getSecondFnUnitSteps(): CtsGantryFunctionalUnitStep[] {
    return this.second ? this.second.functionalUnits.steps : [];
  }

  public getFunctionalUnitNameList(): string[] {
    return this.functionalUnitNameList;
  }

  private initFnUnitNameSet(): void {
    let names: string[] = [];
    if (this.first) {
      this.initFUnitDataMap(this.first, this.firstMap);
      names = names.concat(this.first.functionalUnits.names);
    }

    if (this.second) {
      this.initFUnitDataMap(this.second, this.secondMap);
      names = names.concat(this.second.functionalUnits.names);
    }

    this.fnUnitNameSet = new Set<string>(names);
    this.functionalUnitNameList = Array.from(this.fnUnitNameSet).sort((a, b) =>
      a.localeCompare(b),
    );

    this.firstFnUnitCheckList = [];
    this.secondFnUnitCheckList = [];
    this.functionalUnitNameList.forEach((unitName) => {
      this.fillCheckList(
        unitName,
        this.first,
        this.firstMap,
        this.firstFnUnitCheckList,
      );

      this.fillCheckList(
        unitName,
        this.second,
        this.secondMap,
        this.secondFnUnitCheckList,
      );
    });
  }

  private fillCheckList(
    unitName: string,
    data: CtsGantryFunctionalUnitData | undefined,
    map: Map<string, CtsGantryFunctionalUnit>,
    checkList: CtsGantryFunctionalUnitCheck[],
  ) {
    if (data) {
      const result: CtsGantryFunctionalUnitCheck = {
        fUnit: {
          functionalUnit: unitName,
          initialization: '',
          hwDetect: '',
          hwConfig: '',
          hwFunctionTest: '',
          standBy: '',
        } as CtsGantryFunctionalUnit,
        isInGrid: map.has(unitName),
      };

      const unit = map.get(unitName);
      if (result.isInGrid && unit) {
        result.fUnit = unit;
      }

      checkList.push(result);
    }
  }

  private initFUnitDataMap(
    data: CtsGantryFunctionalUnitData,
    map: Map<string, CtsGantryFunctionalUnit>,
  ): void {
    map.clear();
    if (data) {
      data.functionalUnits.data.forEach((unit) =>
        map.set(unit.functionalUnit, unit),
      );
    }
  }
}
